import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, MessageSquare, LifeBuoy, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Bubble({ msg }) {
  const isUser = msg.from === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${
        isUser
          ? "bg-[var(--surface-3)] text-[var(--text-1)] border border-white/10 rounded-br-md"
          : "bg-[rgba(124,58,237,0.12)] text-[var(--text-1)] border border-[rgba(124,58,237,0.35)] rounded-bl-md"
      }`}>
        <div className="whitespace-pre-wrap break-words">{msg.text}</div>
        <div className="mono text-[10px] text-[var(--text-3)] mt-1 opacity-80">{new Date(msg.at).toLocaleString()}</div>
      </div>
    </div>
  );
}

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/support/my");
      setTickets(r.data.tickets || []);
      if (!selected && r.data.tickets?.length) setSelected(r.data.tickets[0]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Subject and message required");
    setBusy(true);
    try {
      const r = await api.post("/support/ticket", { subject, message });
      toast.success("Ticket created");
      setSubject(""); setMessage(""); setOpenCreate(false);
      setSelected(r.data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/support/ticket/${selected.id}/reply`, { message: reply });
      setSelected(r.data);
      setReply("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reply");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">Support</h1>
          <p className="text-sm text-[var(--text-3)] mt-1">Get help from our team. We typically reply within a few hours.</p>
        </div>
        <Button
          data-testid="support-create-ticket-button"
          onClick={() => setOpenCreate(true)}
          className="text-white hover:opacity-90 font-semibold" style={{ background: "var(--gradient-primary)" }}
        >
          <Plus size={16} className="mr-2" /> New ticket
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6">
        <div className="umakant-card p-3 col-span-12 lg:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] px-2 py-2">Tickets</div>
          {loading ? (
            <div className="space-y-2"><Skeleton className="h-14 bg-white/5" /><Skeleton className="h-14 bg-white/5" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-sm text-[var(--text-3)] text-center py-12">
              <LifeBuoy size={28} className="mx-auto mb-2 opacity-60" />
              No tickets yet.
            </div>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-auto scrollbar-thin" data-testid="support-tickets-list">
              {tickets.map((t) => (
                <button
                  key={t.id}
                  data-testid={`support-ticket-${t.id}`}
                  onClick={() => setSelected(t)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selected?.id === t.id ? "bg-[var(--surface-3)] border border-white/10" : "hover:bg-white/5 border border-transparent"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-semibold text-sm truncate">{t.subject}</div>
                    <Badge className={t.status === "open"
                      ? "bg-[rgba(245,158,11,0.14)] text-[#FCD34D] border border-[rgba(245,158,11,0.22)] hover:bg-[rgba(245,158,11,0.14)] uppercase tracking-[0.14em] text-[10px]"
                      : t.status === "answered"
                        ? "bg-[rgba(34,197,94,0.14)] text-[#86EFAC] border border-[rgba(34,197,94,0.22)] hover:bg-[rgba(34,197,94,0.14)] uppercase tracking-[0.14em] text-[10px]"
                        : "bg-[var(--surface-1)] text-[var(--text-3)] border border-white/10 hover:bg-[var(--surface-1)] uppercase tracking-[0.14em] text-[10px]"}>
                      {t.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-[var(--text-3)] truncate">{t.messages?.[t.messages.length - 1]?.text}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="umakant-card p-5 sm:p-6 col-span-12 lg:col-span-8 min-h-[500px] flex flex-col" data-testid="support-ticket-detail">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-[var(--text-3)] text-sm">
              <MessageSquare size={28} className="mb-2 opacity-60" />
              Select a ticket or create a new one.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 pb-4 border-b border-white/5 mb-4">
                <div>
                  <div className="font-semibold text-lg">{selected.subject}</div>
                  <div className="text-xs text-[var(--text-3)] mono">#{selected.id.slice(0, 8)} · {new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <Badge className={selected.status === "open"
                  ? "bg-[rgba(245,158,11,0.14)] text-[#FCD34D] border border-[rgba(245,158,11,0.22)] hover:bg-[rgba(245,158,11,0.14)] uppercase tracking-[0.14em] text-[10px]"
                  : "bg-[rgba(34,197,94,0.14)] text-[#86EFAC] border border-[rgba(34,197,94,0.22)] hover:bg-[rgba(34,197,94,0.14)] uppercase tracking-[0.14em] text-[10px]"}>
                  {selected.status}
                </Badge>
              </div>
              <div className="flex-1 space-y-3 overflow-auto scrollbar-thin pr-1 max-h-[55vh]">
                {(selected.messages || []).map((m, i) => <Bubble key={i} msg={m} />)}
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 flex gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply..."
                  className="bg-[var(--surface-1)] border-white/10"
                  data-testid="support-reply-input"
                />
                <Button data-testid="support-reply-send-button" onClick={sendReply} disabled={busy} className="text-white hover:opacity-90 font-semibold" style={{ background: "var(--gradient-primary)" }}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : "Send"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New support ticket</DialogTitle>
            <DialogDescription className="text-[var(--text-3)]">Share details and we'll get back to you soon.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-[var(--text-3)] uppercase tracking-[0.14em] mb-2 block">Subject</label>
              <Input data-testid="support-ticket-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-[var(--surface-1)] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-3)] uppercase tracking-[0.14em] mb-2 block">Message</label>
              <Textarea data-testid="support-ticket-message-textarea" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="bg-[var(--surface-1)] border-white/10" />
            </div>
            <Button data-testid="support-ticket-submit-button" onClick={create} disabled={busy} className="w-full text-white hover:opacity-90 font-semibold" style={{ background: "var(--gradient-primary)" }}>
              {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Create ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
